import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Flex,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- useEffect used in block fetch
import { Suspense, lazy, useEffect, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';

import {
  createAction,
  getEditActionForm,
  getFormValues,
  getNoheadersStatus,
  handleActionStepPrevious,
  getPendingDataActionPayload,
  getSelectedDataFileDetails,
  getSyncOptions,
  putActionData,
  setClearSelectedAreasTable,
  setPendingDataActionPayload,
  setSyncOptions,
} from 'redux/DataImportSlice';
import { getDimensionProperties } from 'redux/ItemPageSlice';
/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- fetchBlock used in useEffect */
import { fetchBlock, getBlockDetails } from 'redux/PlanPageSlice';
import Spinner from 'components/Spinner';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  controlHeight,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

import { setStoredActionColor } from '../actionColors';
import colors from '../dataTheme';
import { controlStyle, iconButtonStyle } from '../detailUi';

import {
  FieldLabel,
  StepFooterRow,
  StepHeading,
  StepRadio,
  StepSelect,
  StepSwitch,
  stepInputStyle,
} from './actionStepUi';

const LazyMappingDimensionModal = lazy(() => import('../MappingDimensionModal'));
const LazyMappingDataModal = lazy(() => import('../MappingDataModel'));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FilterCondition {
  property_id: string;
  operator: string;
  value: string;
}

type ActionType = 'dimension' | 'data' | 'forecast';

interface Properties {
  /** Which import type this step is being rendered for. */
  actionType: ActionType;
  setHelpModalSeleceted?: unknown;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DIMENSION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const footerButton = {
  h: controlHeight,
  px: 4,
  minW: '84px',
  borderRadius: PLAN_REVAMP_RADIUS_SM,
  ...PLAN_TEXT.toolbarTab,
  textTransform: 'none',
  width: 'fit-content',
  boxShadow: 'none',
} as const;

const VALUELESS_OPERATORS = new Set(['is_empty', 'is_not_empty']);

// ---------------------------------------------------------------------------
// FilterConditionRow (dimension type)
// ---------------------------------------------------------------------------

const ToggleRow = ({
  title,
  description,
  isChecked,
  onChange,
}: {
  title: string;
  description: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <Flex alignItems='flex-start' gap={3}>
    <StepSwitch
      mt='2px'
      isChecked={isChecked}
      onChange={(event_) => onChange(event_.target.checked)}
    />
    <Box>
      <Text {...PLAN_TEXT.bodyMedium} color={colors.textDark}>
        {title}
      </Text>
      <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt='2px'>
        {description}
      </Text>
    </Box>
  </Flex>
);

interface FilterConditionRowProperties {
  condition: FilterCondition;
  index: number;
  properties: any[];
  onUpdate: (index: number, update: Partial<FilterCondition>) => void;
  onRemove: (index: number) => void;
}

const FilterConditionRow = ({
  condition,
  index,
  properties,
  onUpdate,
  onRemove,
}: FilterConditionRowProperties) => {
  const needsValue = !VALUELESS_OPERATORS.has(condition.operator);
  const isIn = condition.operator === 'in' || condition.operator === 'not_in';

  return (
    <Flex
      direction='column'
      gap={2}
      w='full'
      p={3}
      border='1px solid'
      borderColor={colors.border}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      bg={colors.surface}
    >
      <Flex gap={2}>
        <Box flex={1} minW={0}>
          <StepSelect
            value={condition.property_id}
            placeholder='Property…'
            options={properties.map((p: any) => ({ value: String(p.id), label: p.name }))}
            onChange={(value) => onUpdate(index, { property_id: String(value) })}
          />
        </Box>
        <Box flex={1} minW={0}>
          <StepSelect
            value={condition.operator}
            placeholder='Operator…'
            options={DIMENSION_OPERATORS}
            onChange={(value) => onUpdate(index, { operator: String(value), value: '' })}
          />
        </Box>
      </Flex>
      <Flex gap={2} alignItems='center'>
        {needsValue ? (
          <Input
            value={condition.value}
            onChange={(event_) => onUpdate(index, { value: event_.target.value })}
            placeholder={isIn ? 'value1, value2 (comma separated)' : 'Value'}
            {...stepInputStyle}
          />
        ) : (
          <Box flex={1} />
        )}
        <Flex
          as='button'
          type='button'
          aria-label='Remove condition'
          title='Remove condition'
          {...iconButtonStyle}
          color={colors.danger}
          onClick={() => onRemove(index)}
        >
          <FiTrash2 size={15} />
        </Flex>
      </Flex>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// DimensionSyncOptions — remove missing items config
// ---------------------------------------------------------------------------

interface DimensionSyncOptionsProperties {
  properties: any[];
  syncOptions: any;
  onSyncOptionsChange: (options: any) => void;
}

const DimensionSyncOptions = ({
  properties,
  syncOptions,
  onSyncOptionsChange,
}: DimensionSyncOptionsProperties) => {
  const rm = syncOptions?.remove_missing ?? { enabled: false };
  const enabled: boolean = rm.enabled ?? false;
  const scopeMode: string = rm.scope_mode ?? 'all';
  const [conditions, setConditions] = useState<FilterCondition[]>(rm.filter?.conditions ?? []);

  const update = (patch: Partial<typeof rm>) => {
    const newRm = { ...rm, ...patch };
    onSyncOptionsChange({ remove_missing: newRm });
  };

  const handleToggle = (checked: boolean) => {
    update({ enabled: checked, scope_mode: checked ? scopeMode : 'all' });
    if (!checked) setConditions([]);
  };

  const handleScopeChange = (value: string) => {
    update({ scope_mode: value });
    if (value === 'all') setConditions([]);
  };

  const pushConditions = (next: FilterCondition[]) => {
    setConditions(next);
    const filter = next.length > 0 ? { logic: 'AND' as const, conditions: next } : undefined;
    update({ filter });
  };

  return (
    <Flex direction='column' gap={4} w='full'>
      <ToggleRow
        title='Remove missing items'
        description='Remove items not present in this file after the import completes.'
        isChecked={enabled}
        onChange={handleToggle}
      />

      <Collapse in={enabled} animateOpacity style={{ width: '100%' }}>
        <Flex direction='column' gap={3} w='full' pt={1}>
          <FieldLabel mb={0}>Removal scope</FieldLabel>
          <Flex direction='column' gap={2}>
            <StepRadio isSelected={scopeMode === 'all'} onSelect={() => handleScopeChange('all')}>
              All items in this dimension
            </StepRadio>
            <StepRadio
              isSelected={scopeMode === 'filter'}
              onSelect={() => handleScopeChange('filter')}
            >
              Only remove items matching filters
            </StepRadio>
          </Flex>

          {scopeMode === 'filter' && (
            <Flex direction='column' gap={2} w='full' pt={1}>
              <FieldLabel mb={0}>Filter conditions (AND logic)</FieldLabel>
              {conditions.length === 0 && (
                <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
                  No conditions — all items are eligible. Add a condition to restrict.
                </Text>
              )}
              {conditions.map((c, conditionIndex) => (
                <FilterConditionRow
                  key={conditionIndex}
                  condition={c}
                  index={conditionIndex}
                  properties={properties}
                  onUpdate={(targetIndex, up) =>
                    pushConditions(
                      conditions.map((x, index_) => (index_ === targetIndex ? { ...x, ...up } : x)),
                    )
                  }
                  onRemove={(targetIndex) =>
                    pushConditions(conditions.filter((_, index_) => index_ !== targetIndex))
                  }
                />
              ))}
              <Flex
                as='button'
                type='button'
                {...controlStyle}
                alignSelf='flex-start'
                onClick={() =>
                  pushConditions([
                    ...conditions,
                    {
                      property_id: properties?.[0]?.id ? String(properties[0].id) : '',
                      operator: 'equals',
                      value: '',
                    },
                  ])
                }
              >
                <FiPlus size={14} />
                Add condition
              </Flex>
            </Flex>
          )}
        </Flex>
      </Collapse>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// DataForecastSyncOptions — clear before import config
// ---------------------------------------------------------------------------

interface DimScope {
  dimension_id: number;
  name: string;
  is_time: boolean;
  mode: 'matching' | 'all';
}

interface DataForecastSyncOptionsProperties {
  blockDimensions: DimScope[];
  syncOptions: any;
  onSyncOptionsChange: (options: any) => void;
}

const DataForecastSyncOptions = ({
  blockDimensions,
  syncOptions,
  onSyncOptionsChange,
}: DataForecastSyncOptionsProperties) => {
  const callback = syncOptions?.clear_before_import ?? { enabled: false };
  const enabled: boolean = callback.enabled ?? false;

  const defaultScopes = (): DimScope[] =>
    blockDimensions.map((d) => ({ ...d, mode: d.is_time ? 'matching' : 'all' }));

  const savedScopes: DimScope[] = (() => {
    if (!callback.dimension_scopes?.length) return defaultScopes();
    return blockDimensions.map((bd) => {
      const saved = callback.dimension_scopes.find((s: any) => s.dimension_id === bd.dimension_id);
      return { ...bd, mode: saved?.mode ?? (bd.is_time ? 'matching' : 'all') };
    });
  })();

  const [dimScopes, setDimScopes] = useState<DimScope[]>(savedScopes);

  const update = (patch: any) => {
    onSyncOptionsChange({ clear_before_import: { ...callback, ...patch } });
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      const scopes = dimScopes.map((d) => ({ dimension_id: d.dimension_id, mode: d.mode }));
      update({ enabled: true, dimension_scopes: scopes });
    } else {
      update({ enabled: false, dimension_scopes: [] });
    }
  };

  const handleScopeChange = (dimId: number, mode: 'matching' | 'all') => {
    const next = dimScopes.map((d) => (d.dimension_id === dimId ? { ...d, mode } : d));
    setDimScopes(next);
    update({
      dimension_scopes: next.map((d) => ({ dimension_id: d.dimension_id, mode: d.mode })),
    });
  };

  return (
    <Flex direction='column' gap={4} w='full'>
      <ToggleRow
        title='Clear before import'
        description='Remove existing values before loading so this import fully reconciles with the source file.'
        isChecked={enabled}
        onChange={handleToggle}
      />

      <Collapse in={enabled} animateOpacity style={{ width: '100%' }}>
        {blockDimensions.length > 0 ? (
          <Flex direction='column' gap={2} w='full' pt={1}>
            <FieldLabel mb={0}>Clearing scope for each dimension</FieldLabel>
            <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
              Matching clears only values present in the file for this dimension. All clears every
              existing value for it.
            </Text>
            {dimScopes.map((dim) => (
              <Flex
                key={dim.dimension_id}
                direction='column'
                gap={2}
                p={3}
                border='1px solid'
                borderColor={colors.border}
                borderRadius={PLAN_REVAMP_RADIUS_SM}
              >
                <Text {...PLAN_TEXT.bodyMedium} color={colors.textDark}>
                  {dim.name}
                </Text>
                <Flex gap={5} flexWrap='wrap'>
                  <StepRadio
                    isSelected={dim.mode === 'matching'}
                    onSelect={() => handleScopeChange(dim.dimension_id, 'matching')}
                  >
                    {dim.is_time ? 'Matching periods' : 'Matching items'}
                  </StepRadio>
                  <StepRadio
                    isSelected={dim.mode === 'all'}
                    onSelect={() => handleScopeChange(dim.dimension_id, 'all')}
                  >
                    {dim.is_time ? 'All periods' : 'All items'}
                  </StepRadio>
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
            Dimension information not available. Ensure a block is selected in Step 2.
          </Text>
        )}
      </Collapse>
    </Flex>
  );
};

// ---------------------------------------------------------------------------
// ActionStepSyncOptions — main export
// ---------------------------------------------------------------------------

/**
 * Final wizard step for all import action types.
 *
 * Renders "Remove missing items" config for dimensions and
 * "Clear before import" with per-dimension scope for data/forecast.
 *
 * On submit: creates/updates the action with all accumulated settings
 * (including the property mapping stored in Redux from step 3) and opens the
 * mapping preview modal.
 */
const ActionStepSyncOptions = ({ actionType }: Properties) => {
  const dispatch: any = useDispatch();
  const getFormValue = useSelector(getFormValues);
  const sourceFileId = useSelector(getSelectedDataFileDetails);
  const stepperFormEditable = useSelector(getEditActionForm);
  const isHeader = useSelector(getNoheadersStatus);
  const properties = useSelector(getDimensionProperties);
  const syncOptions = useSelector(getSyncOptions);
  const blockDetails = useSelector(getBlockDetails);
  const pendingDataActionPayload = useSelector(getPendingDataActionPayload);

  const [mappingModal, setMappingModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);
  const [selectedMapType, setSelectedMapType] = useState('');
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const toast = useToast();

  // Ensure block details are loaded when on Sync Options (data/forecast flow)
  const blockId = Number(getFormValue?.blockId);
  useEffect(() => {
    if (actionType !== 'dimension' && blockId && (!blockDetails || blockDetails.id !== blockId)) {
      dispatch(fetchBlock(blockId));
    }
  }, [actionType, blockId, blockDetails?.id, dispatch]);

  // Build dimension scope list from selected block (for data/forecast).
  // Use getBlockDetails (populated by fetchBlock in DataStepsForm) — has full dimensions.
  const blockDimensions: DimScope[] = (() => {
    if (actionType === 'dimension') return [];
    if (!blockId) return [];
    const block = blockDetails;
    if (!block || block.id !== blockId || !block?.dimensions) return [];
    return (block.dimensions as any[]).map((d: any) => ({
      dimension_id: d.id,
      name: d.name,
      is_time: d.name?.toLowerCase() === 'time',
      mode: d.name?.toLowerCase() === 'time' ? 'matching' : 'all',
    }));
  })();

  const handleSyncOptionsChange = (options: any) => {
    dispatch(setSyncOptions(options));
  };

  const persistColor = (actionId: number | string | null | undefined) => {
    if (actionId && getFormValue?.color) setStoredActionColor(actionId, getFormValue.color);
  };

  const handleSubmit = async () => {
    setSaveError(null);
    setIsSubmitting(true);
    try {
      /**
       * Dev-only: fake a failed save (same UX as HTTP 500) without hitting the API.
       * In the browser console: localStorage.setItem('DEBUG_FORCE_ACTION_SAVE_ERROR', '1')
       * To turn off: localStorage.removeItem('DEBUG_FORCE_ACTION_SAVE_ERROR')
       */
      if (
        process.env.NODE_ENV === 'development' &&
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('DEBUG_FORCE_ACTION_SAVE_ERROR') === '1'
      ) {
        throw new Error(
          'Simulated server error (HTTP 500). Remove localStorage key DEBUG_FORCE_ACTION_SAVE_ERROR to disable.',
        );
      }

      if (actionType === 'dimension') {
        // Dimension: build request from form values
        const dimId = getFormValue?.dimension;
        const requestBody: any = {
          name: getFormValue?.name,
          description: getFormValue?.description,
          action_type: 'dimension',
          mapping_type: 'row',
          time_column: 'time',
          period_type: 'single',
          missing_header: isHeader,
          sync_options: syncOptions ?? null,
          dimension_id: dimId,
          mappings: {
            [dimId]: getFormValue?.column,
            property_map: getFormValue?.propertdata ?? {},
          },
        };
        if (stepperFormEditable?.isEditForm) {
          await dispatch(
            putActionData({
              dataSourceId: sourceFileId?.id,
              actionId: stepperFormEditable?.actionId,
              request_body: requestBody,
            }),
          ).unwrap();
          persistColor(stepperFormEditable?.actionId);
          setSelectedActionId(stepperFormEditable?.actionId);
          setSelectedMapType(stepperFormEditable?.actionType);
          setScenarioId(stepperFormEditable?.scenarioId);
        } else {
          const created = await dispatch(
            createAction({ dataSourceId: sourceFileId?.id, request_body: requestBody }),
          ).unwrap();
          persistColor(created?.id);
          setSelectedActionId(created?.id);
          setSelectedMapType(created?.action_type);
          setScenarioId(created?.scenario_id);
        }
        dispatch(setClearSelectedAreasTable());
        setMappingModal(true);
      } else {
        // Data/forecast: use pending payload from DimensionSelectionSteps, merge sync_options
        const pending = pendingDataActionPayload;
        if (!pending?.request_body) {
          const message = 'Import configuration is missing. Go back a step and try again.';
          setSaveError(message);
          toast({
            title: 'Cannot continue',
            description: message,
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        const requestBody = {
          ...pending.request_body,
          sync_options: syncOptions ?? null,
        };
        if (pending.isEditForm && pending.actionId) {
          await dispatch(
            putActionData({
              dataSourceId: sourceFileId?.id,
              actionId: pending.actionId,
              request_body: requestBody,
            }),
          ).unwrap();
          persistColor(pending.actionId);
          setSelectedActionId(pending.actionId);
          setSelectedMapType(stepperFormEditable?.actionType ?? 'data');
          setScenarioId(getFormValue?.selectedScenario);
        } else {
          const created = await dispatch(
            createAction({ dataSourceId: sourceFileId?.id, request_body: requestBody }),
          ).unwrap();
          persistColor(created?.id);
          setSelectedActionId(created?.id);
          setSelectedMapType(created?.action_type);
          setScenarioId(created?.scenario_id);
        }
        dispatch(setPendingDataActionPayload(null));
        dispatch(setClearSelectedAreasTable());
        setMappingModal(true);
      }
    } catch (error: unknown) {
      const message =
        typeof error === 'string'
          ? error
          : error instanceof Error && error.message
          ? error.message
          : 'Something went wrong while saving the action. Please try again, or contact support if it keeps happening.';
      setSaveError(message);
      toast({
        title: 'Could not save the action',
        description: message,
        status: 'error',
        duration: 12_000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex direction='column' w='100%'>
      <StepHeading
        title='Sync options'
        subtitle={
          actionType === 'dimension'
            ? 'Decide what happens to items that are not in this file.'
            : 'Decide whether existing values are cleared before this import.'
        }
      />

      {actionType === 'dimension' ? (
        <DimensionSyncOptions
          properties={properties ?? []}
          syncOptions={syncOptions}
          onSyncOptionsChange={handleSyncOptionsChange}
        />
      ) : (
        <DataForecastSyncOptions
          blockDimensions={blockDimensions}
          syncOptions={syncOptions}
          onSyncOptionsChange={handleSyncOptionsChange}
        />
      )}

      {saveError && (
        <Alert status='error' borderRadius={PLAN_REVAMP_RADIUS_SM} alignItems='flex-start' mt={4}>
          <AlertIcon mt='2px' />
          <Box>
            <AlertTitle {...PLAN_TEXT.bodyMedium}>Could not save the action</AlertTitle>
            <AlertDescription {...PLAN_TEXT.labelSm} mt={1}>
              {saveError} You can adjust sync options or go back to check mappings, then try
              Continue again.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <StepFooterRow>
        <Flex gap={2}>
          <Button
            {...footerButton}
            variant='outline'
            borderColor={colors.border}
            bg={colors.surface}
            color={colors.textDark}
            _hover={{ bg: colors.hover }}
            onClick={() => dispatch(handleActionStepPrevious())}
          >
            Previous
          </Button>
          <Button
            {...footerButton}
            bg={colors.action}
            color={colors.white}
            _hover={{ bg: colors.actionHover }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText='Saving…'
          >
            Continue
          </Button>
        </Flex>
      </StepFooterRow>

      <Suspense fallback={<Spinner />}>
        {actionType === 'dimension' ? (
          <LazyMappingDimensionModal
            mappingModal={mappingModal}
            setMappingModal={{ on: () => setMappingModal(true), off: () => setMappingModal(false) }}
            selectedActionId={selectedActionId}
            dataSourceId={sourceFileId?.id}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        ) : (
          <LazyMappingDataModal
            mappingModal={mappingModal}
            setMappingModal={{ on: () => setMappingModal(true), off: () => setMappingModal(false) }}
            selectedActionId={selectedActionId}
            dataSourceId={sourceFileId?.id}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        )}
      </Suspense>
    </Flex>
  );
};

export default ActionStepSyncOptions;
